import React, { useState, useEffect, useMemo, useRef } from 'react';
import { CategoryItem, TreeItem } from '../types';
import { TreeNode } from './TreeNode';
import { AddItemInput } from './AddItemInput';
import { AICardForm } from './AICardForm';
import { CategoryPracticePhrasesSession } from './CategoryPracticePhrasesSession';
import { CategoryPracticeSession } from './CategoryPracticeSession';
import { GenerateStoryModal } from './GenerateStoryModal';
import { PracticePickerModal } from './PracticePickerModal';
import { AddTextModal } from './AddTextModal';
import { ReviseStoryModal } from './ReviseStoryModal';
import { ContextualPracticeModal } from './ContextualPracticeModal';
import { DropdownMenu, DropdownMenuItem, createEditAction, createDeleteAction } from './DropdownMenu';
import { MoveToCollectionModal } from './MoveToCollectionModal';
import { handleConditionalDelete } from '../utils/deleteUtils';
import { speak, stopSpeech } from '../utils/speechUtils';
import { X, Tag, Loader2, Folder, Plus, Check, Sparkles, FolderInput, BookText, Trash2, ChevronDown, ChevronUp, Volume2, Square, Zap, Pencil } from 'lucide-react';
import { apiClient } from '../services/api';
import { useInstance } from '../hooks/useInstance';
import { useAuth } from '../hooks/useAuth';
import { SHOW_PRACTICE_PHRASES_IN_MENU } from '../constants/uiVisibility';
import { buildTree, flattenTree, updateTreeItem } from '../utils/treeUtils';
import { SavedStory } from '../services/api';
import { highlightWordsWithLinks } from '../utils/highlightWords';
import { CardDetail } from './CardDetail';

interface CategoryViewProps {
  category: CategoryItem;
  onClose: () => void;
  onRename: (id: number, newName: string) => void;
  onDelete: (id: number) => void;
  onMove: (itemId: number, data: { parent_id?: number | null; category_id?: number | null }) => Promise<void>;
  onCategoryRefresh: (categoryId: number, expandFolderId?: number) => void;
  categories?: CategoryItem[];
  isSelectionMode?: boolean;
  selectedCardIds?: Set<number>;
  onSelectCard?: (id: number, selected: boolean) => void;
}

export const CategoryView: React.FC<CategoryViewProps> = ({
  category,
  onClose,
  onRename,
  onDelete,
  onMove,
  onCategoryRefresh,
  categories = [],
  isSelectionMode = false,
  selectedCardIds = new Set(),
  onSelectCard,
}) => {
  const { selectedInstanceId, instancesWithRoles } = useInstance();
  const { user } = useAuth();
  const [categoryData, setCategoryData] = useState<CategoryItem>(category);
  const [isLoading, setIsLoading] = useState(false);
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
  const [allCategories, setAllCategories] = useState<CategoryItem[]>(categories);
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(category.name);
  const [showAddInput, setShowAddInput] = useState(false);
  const [addType, setAddType] = useState<'folder' | 'card'>('folder');
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showAICardForm, setShowAICardForm] = useState(false);
  const [showPracticePhrasesSession, setShowPracticePhrasesSession] = useState(false);
  const [showPracticePicker, setShowPracticePicker] = useState(false);
  const [showPracticeCategorySession, setShowPracticeCategorySession] = useState(false);
  const [showContextualPractice, setShowContextualPractice] = useState(false);
  const isInstanceOwner = useMemo(() => {
    if (!selectedInstanceId || !user) return false;
    const found = instancesWithRoles.find(i => i.instance.id === selectedInstanceId);
    return found?.user_role === 'owner' || found?.instance.created_by === user.id;
  }, [selectedInstanceId, instancesWithRoles, user]);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [showGenerateStory, setShowGenerateStory] = useState(false);
  const [stories, setStories] = useState<SavedStory[]>([]);
  const [isLoadingStories, setIsLoadingStories] = useState(false);
  const [expandedStoryIds, setExpandedStoryIds] = useState<Set<number>>(new Set());
  const [confirmDeleteStoryId, setConfirmDeleteStoryId] = useState<number | null>(null);
  const [deletingStoryId, setDeletingStoryId] = useState<number | null>(null);
  const [speakingStoryId, setSpeakingStoryId] = useState<number | null>(null);
  const storiesSectionRef = useRef<HTMLDivElement>(null);
  const scrollableContentRef = useRef<HTMLDivElement>(null);
  const [storyError, setStoryError] = useState<string | null>(null);
  const [storiesSectionVisible, setStoriesSectionVisible] = useState(false);

  useEffect(() => {
    const el = storiesSectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setStoriesSectionVisible(entry.isIntersecting),
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [storiesSectionRef.current]);
  const [editingStory, setEditingStory] = useState<SavedStory | null>(null);
  const [revisingStory, setRevisingStory] = useState<SavedStory | null>(null);
  const [showAddText, setShowAddText] = useState(false);
  const [openStoryCardId, setOpenStoryCardId] = useState<number | null>(null);


  const loadStories = async () => {
    setIsLoadingStories(true);
    try {
      const data = await apiClient.listStories(category.id);
      setStories(data.items);
    } catch {
      // silently fail — stories section shows empty
    } finally {
      setIsLoadingStories(false);
    }
  };

  useEffect(() => {
    return () => { stopSpeech(); };
  }, []);

  useEffect(() => {
    if (selectedInstanceId !== null) {
      loadCategoryData();
      loadAllCategories();
      loadStories();
    }
  }, [category.id, selectedInstanceId]);

  // Sync editName with category.name when category changes
  useEffect(() => {
    if (!isEditing) {
      setEditName(categoryData.name);
    }
  }, [categoryData.name, isEditing]);

  const loadAllCategories = async () => {
    if (selectedInstanceId === null) return;
    try {
      const cats = await apiClient.getCategories(selectedInstanceId);
      setAllCategories(cats.map((cat) => ({
        id: cat.id,
        name: cat.name,
        isExpanded: false,
        children: [],
      })));
    } catch (err) {
      console.error('Error loading categories:', err);
    }
  };

  const loadCategoryData = async () => {
    if (selectedInstanceId === null) return;
    try {
      setIsLoading(true);
      const cards = await apiClient.getCardsHierarchy(category.id, selectedInstanceId);
      const treeData = buildTree(cards, expandedIds);
      
      setCategoryData({
        ...category,
        children: treeData,
        isExpanded: true,
      });
    } catch (err: any) {
      console.error('Error loading category data:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggle = (id: number) => {
    setExpandedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      
      // Update the tree items with new expansion state
      const updateTreeExpansion = (items: TreeItem[]): TreeItem[] => {
        return items.map((item) => ({
          ...item,
          isExpanded: newSet.has(item.id),
          children: item.children ? updateTreeExpansion(item.children) : [],
        }));
      };
      
      setCategoryData((prev) => ({
        ...prev,
        children: updateTreeExpansion(prev.children),
      }));
      
      return newSet;
    });
  };

  const handleAddChildToFolder = async (parentId: number, type: 'folder' | 'card', name: string) => {
    try {
      await apiClient.createCard({
        name,
        parent_id: parentId,
        is_folder: type === 'folder',
        category_id: category.id,
      });

      // Refresh the category data
      await loadCategoryData();
    } catch (err: any) {
      console.error('Error adding child to folder:', err);
      const errorMessage = err.message || err.response?.data?.detail || 'Failed to add item';
      alert(`Failed to add item: ${errorMessage}`);
    }
  };

  const handleCategoryRefresh = async () => {
    await loadCategoryData();
  };

  const handleChildRename = (id: number, newName: string) => {
    setCategoryData(prev => ({
      ...prev,
      children: updateTreeItem(prev.children, id, { name: newName }),
    }));
    onRename(id, newName);
  };

  const handleRename = async () => {
    if (!editName.trim()) {
      setEditName(categoryData.name);
      setIsEditing(false);
      return;
    }

    if (editName.trim() === categoryData.name) {
      setIsEditing(false);
      return;
    }

    try {
      setIsSaving(true);
      await apiClient.updateCategory(categoryData.id, { name: editName.trim() });
      onRename(categoryData.id, editName.trim());
      setCategoryData({ ...categoryData, name: editName.trim() });
      setIsEditing(false);
    } catch (err: any) {
      console.error('Error renaming category:', err);
      setEditName(categoryData.name); // Reset on error
      alert('Failed to rename category. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditName(categoryData.name);
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleRename();
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const relatedTarget = e.relatedTarget as HTMLElement;
    if (relatedTarget && (relatedTarget.closest('.edit-actions') || relatedTarget.closest('button'))) {
      return;
    }
    setTimeout(() => {
      if (isEditing) {
        handleRename();
      }
    }, 200);
  };

  const handleDelete = async () => {
    const categoryAsTreeItem = {
      id: categoryData.id,
      name: categoryData.name,
      type: 'folder' as const,
      parent_id: null,
      is_folder: true,
      children: categoryData.children,
      isExpanded: false,
      isCategory: true,
    };

    try {
      setIsDeleting(true);
      await handleConditionalDelete(
        categoryAsTreeItem,
        () => {
          onDelete(categoryData.id);
          onClose();
        },
        (error) => {
          console.error('Error deleting category:', error);
          alert('Failed to delete category');
        }
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const handleAddChild = async (name: string) => {
    try {
      await apiClient.createCard({
        name,
        parent_id: null,
        is_folder: addType === 'folder',
        category_id: categoryData.id,
      });

      await loadCategoryData();
      setShowAddInput(false);
    } catch (err: any) {
      console.error('Error adding child:', err);
      alert('Failed to add item');
    }
  };

  const handleAddFolder = () => {
    setAddType('folder');
    setShowAddInput(true);
  };

  const handleAddCard = () => {
    setAddType('card');
    setShowAddInput(true);
  };

  const handleAICardSuccess = (cardId: number) => {
    setShowAICardForm(false);
    loadCategoryData();
  };

  const handleDeleteStory = async (storyId: number) => {
    setDeletingStoryId(storyId);
    setStoryError(null);
    try {
      await apiClient.deleteStory(category.id, storyId);
      setStories(prev => prev.filter(s => s.id !== storyId));
      setConfirmDeleteStoryId(null);
    } catch (err: any) {
      if (err.response?.status === 403) {
        setStoryError('You can only delete texts you created.');
      } else {
        setStoryError('Could not delete the text. Please try again.');
      }
    } finally {
      setDeletingStoryId(null);
    }
  };

  const categoryLeafCards = useMemo(
    () =>
      flattenTree(categoryData.children)
        .filter((item) => !item.is_folder)
        .map((item) => ({ id: item.id, name: item.name })),
    [categoryData.children]
  );

  // Create dropdown menu items for category
  const getCategoryDropdownItems = (): DropdownMenuItem[] => {
    const items: DropdownMenuItem[] = [];


    if (isInstanceOwner) {
      items.push(
        {
          id: 'add-folder',
          label: 'Add folder',
          icon: <Folder className="h-4 w-4" />,
          onClick: handleAddFolder,
        },
        {
          id: 'add-card',
          label: 'Add card',
          icon: <Plus className="h-4 w-4" />,
          onClick: handleAddCard,
        },
        createEditAction(() => setIsEditing(true)),
        {
          id: 'move-to-collection',
          label: 'Move to Collection',
          icon: <FolderInput className="h-4 w-4" />,
          onClick: () => setShowMoveModal(true),
        },
        createDeleteAction(handleDelete)
      );
    }

    if (SHOW_PRACTICE_PHRASES_IN_MENU && categoryData.id > 0) {
      items.push({
        id: 'practice-phrases',
        label: 'Practice Phrases',
        icon: <Sparkles className="h-4 w-4" />,
        onClick: () => setShowPracticePhrasesSession(true),
      });
    }

    return items;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-indigo-50">
          <div className="flex items-center space-x-3 flex-1 min-w-0">
            <div className="hidden sm:flex p-2 bg-purple-100 rounded-lg flex-shrink-0">
              <Tag className="h-5 w-5 sm:h-6 sm:w-6 text-purple-600" />
            </div>
            <div className="flex-1 min-w-0">
              {isEditing ? (
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    onBlur={handleBlur}
                    onKeyDown={handleKeyDown}
                    className="flex-1 border border-purple-300 rounded-md px-3 py-1 text-lg sm:text-xl font-bold bg-white text-purple-900 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    autoFocus
                    disabled={isSaving}
                  />
                  <div className="edit-actions flex items-center space-x-1">
                    <button
                      onClick={handleRename}
                      disabled={isSaving || !editName.trim()}
                      className="p-1.5 hover:bg-green-100 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Save"
                    >
                      <Check className="h-5 w-5 text-green-600" />
                    </button>
                    <button
                      onClick={handleCancelEdit}
                      disabled={isSaving}
                      className="p-1.5 hover:bg-red-100 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Cancel"
                    >
                      <X className="h-5 w-5 text-red-600" />
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <h2 className="text-lg sm:text-xl font-bold text-purple-900 truncate">
                    {categoryData.name}
                  </h2>
                  <p className="text-xs sm:text-sm text-purple-600 mt-1">
                    {categoryData.children.length} {categoryData.children.length === 1 ? 'item' : 'items'}
                  </p>
                </>
              )}
            </div>
          </div>
          
          {/* Add buttons - only for instance owners, desktop only */}
          {!isEditing && isInstanceOwner && (
            <div className="hidden sm:flex items-center space-x-2 mr-2">
              <button
                onClick={handleAddFolder}
                className="p-2 hover:bg-blue-100 rounded-lg bg-blue-50 transition-colors"
                title="Add folder"
              >
                <Folder className="h-4 w-4 text-blue-600" />
              </button>
              <button
                onClick={handleAddCard}
                className="p-2 hover:bg-gray-200 rounded-lg bg-gray-100 transition-colors"
                title="Add card"
              >
                <Plus className="h-4 w-4 text-gray-700" />
              </button>
            </div>
          )}

          {!isEditing && (
            <button
              onClick={() => setShowPracticePicker(true)}
              className="p-2 bg-amber-50 hover:bg-amber-100 rounded-lg transition-colors flex-shrink-0 ml-2"
              title="Actions"
            >
              <Zap className="h-4 w-4 text-amber-600" />
            </button>
          )}

          {/* Actions dropdown menu */}
          {!isEditing && (
            <div className="ml-2">
              <DropdownMenu
                items={getCategoryDropdownItems()}
                className="opacity-100"
                disabled={isDeleting}
              />
            </div>
          )}

          <button
            onClick={() => { stopSpeech(); onClose(); }}
            className="p-2 hover:bg-purple-100 rounded-lg transition-colors flex-shrink-0 ml-2"
            title="Close"
          >
            <X className="h-5 w-5 text-purple-600" />
          </button>
        </div>

        {/* Add item input */}
        {showAddInput && (
          <div className="px-4 sm:px-6 py-4 bg-gray-50 border-b border-gray-100">
            <AddItemInput
              parentId={null}
              type={addType}
              onSubmit={handleAddChild}
              onCancel={() => setShowAddInput(false)}
              placeholder={`Add ${addType}...`}
            />
          </div>
        )}

        {/* Content */}
        <div ref={scrollableContentRef} className="flex-1 overflow-y-auto p-4 sm:p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-purple-600" />
              <span className="ml-2 text-gray-600">Loading category...</span>
            </div>
          ) : categoryData.children.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-500">
              <Folder className="h-12 w-12 mb-4 opacity-50" />
              <p className="text-lg font-medium mb-2">No items in this category</p>
              <p className="text-sm text-center mb-4">Add folders or cards to get started</p>
              {isInstanceOwner && (
                <div className="flex items-center justify-center space-x-2">
                  <button
                    onClick={handleAddFolder}
                    className="px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                  >
                    Add Folder
                  </button>
                  <button
                    onClick={handleAddCard}
                    className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                  >
                    Add Card
                  </button>
                  <button
                    onClick={() => setShowAICardForm(true)}
                    className="px-4 py-2 text-sm font-medium text-purple-600 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors"
                  >
                    <Sparkles className="h-4 w-4 inline mr-1" />
                    Add with AI
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {categoryData.children.map((item) => (
                <TreeNode
                  key={item.id}
                  item={item}
                  onToggle={handleToggle}
                  onRename={handleChildRename}
                  onDelete={onDelete}
                  onAddChild={handleAddChildToFolder}
                  onMove={onMove}
                  level={0}
                  categoryId={category.id}
                  categories={allCategories}
                  isInstanceOwner={isInstanceOwner}
                  isSelectionMode={isSelectionMode}
                  selectedCardIds={selectedCardIds}
                  onSelectCard={onSelectCard}
                />
              ))}
            </div>
          )}

          {/* Saved Stories */}
          <div ref={storiesSectionRef} className="mt-8 pt-6 border-t border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Saved Texts</h3>
              {isInstanceOwner && (
                <button
                  type="button"
                  onClick={() => setShowAddText(true)}
                  className="p-1 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-full transition-colors"
                  title="Add text"
                >
                  <Plus className="h-4 w-4" />
                </button>
              )}
            </div>
            {isLoadingStories ? (
              <div className="flex justify-center py-4">
                <Loader2 className="h-5 w-5 animate-spin text-gray-300" />
              </div>
            ) : stories.length === 0 ? (
              <p className="text-sm text-gray-400 italic">No texts saved yet.</p>
            ) : (
              <div className="space-y-3">
                {storyError && (
                  <div className="bg-red-50 text-red-700 p-3 rounded-md text-sm">{storyError}</div>
                )}
                {stories.map(story => (
                  <div key={story.id} className="border border-gray-200 rounded-lg overflow-hidden">
                    <div
                      className="flex items-start justify-between gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors"
                      onClick={() => setExpandedStoryIds(prev => {
                        const next = new Set(prev);
                        next.has(story.id) ? next.delete(story.id) : next.add(story.id);
                        return next;
                      })}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-800 truncate">{story.title}</p>
                        <p className="text-xs text-gray-400 mt-0.5">
                          {new Date(story.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                        </p>
                      </div>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        {/* Chevron — visual indicator only */}
                        <span className="p-1.5 text-gray-400">
                          {expandedStoryIds.has(story.id)
                            ? <ChevronUp className="h-4 w-4" />
                            : <ChevronDown className="h-4 w-4" />}
                        </span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const isPlaying = speakingStoryId === story.id;
                            if (isPlaying) {
                              stopSpeech();
                              setSpeakingStoryId(null);
                            } else {
                              speak(story.story, {
                                onEnd: () => setSpeakingStoryId(null),
                                onError: () => setSpeakingStoryId(null),
                              });
                              setSpeakingStoryId(story.id);
                            }
                          }}
                          className={`p-1.5 rounded-full transition-colors ${speakingStoryId === story.id ? 'text-indigo-600 bg-indigo-50 hover:bg-indigo-100' : 'text-gray-400 hover:text-indigo-600 hover:bg-indigo-50'}`}
                          title={speakingStoryId === story.id ? 'Stop' : 'Read aloud'}
                        >
                          {speakingStoryId === story.id ? <Square className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
                        </button>
                        {story.user_created === user?.id && (
                          confirmDeleteStoryId === story.id ? (
                            <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                              <span className="text-xs text-gray-500">Sure?</span>
                              <button
                                onClick={() => handleDeleteStory(story.id)}
                                disabled={deletingStoryId === story.id}
                                className="text-xs text-red-600 hover:underline disabled:opacity-50"
                              >
                                {deletingStoryId === story.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Yes'}
                              </button>
                              <button
                                onClick={() => setConfirmDeleteStoryId(null)}
                                className="text-xs text-gray-500 hover:underline"
                              >
                                No
                              </button>
                            </div>
                          ) : (
                            <>
                              <button
                                onClick={(e) => { e.stopPropagation(); setRevisingStory(story); }}
                                className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-full transition-colors"
                                title="Revise"
                              >
                                <Sparkles className="h-4 w-4" />
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); setEditingStory(story); }}
                                className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-full transition-colors"
                                title="Edit"
                              >
                                <Pencil className="h-4 w-4" />
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); setConfirmDeleteStoryId(story.id); }}
                                className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-full transition-colors"
                                title="Delete"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </>
                          )
                        )}
                      </div>
                    </div>
                    {expandedStoryIds.has(story.id) && (
                      <div className="px-4 pb-4 border-t border-gray-100 pt-3">
                        <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{highlightWordsWithLinks(story.story, story.words_used, setOpenStoryCardId)}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Saved texts pill — only when stories exist and section is off-screen */}
        {stories.length > 0 && !isLoadingStories && !storiesSectionVisible && (
          <div className="flex justify-center py-2.5 border-t border-gray-100 bg-white rounded-b-lg">
            <button
              onClick={() => storiesSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
              className="flex items-center gap-1.5 px-4 py-1.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 text-xs font-medium rounded-full transition-colors"
            >
              <BookText className="h-3.5 w-3.5" />
              {stories.length} saved text{stories.length !== 1 ? 's' : ''} ↓
            </button>
          </div>
        )}

        {/* AI Card Form Modal */}
        {showAICardForm && (
          <AICardForm
            categoryId={categoryData.id}
            categoryName={categoryData.name}
            onClose={() => setShowAICardForm(false)}
            onSuccess={handleAICardSuccess}
          />
        )}

        {showPracticePicker && (
          <PracticePickerModal
            categoryName={categoryData.name}
            onClose={() => setShowPracticePicker(false)}
            onFlashcards={() => setShowPracticeCategorySession(true)}
            onContextual={() => setShowContextualPractice(true)}
            onGenerateText={() => setShowGenerateStory(true)}
            hasCards={categoryLeafCards.length > 0}
            hasMultipleCards={categoryLeafCards.length > 1}
            isOwner={isInstanceOwner}
            onAICard={isInstanceOwner ? () => setShowAICardForm(true) : undefined}
            onAddText={isInstanceOwner ? () => setShowAddText(true) : undefined}
          />
        )}

        {showPracticeCategorySession && categoryData.id > 0 && (
          <CategoryPracticeSession
            name={categoryData.name}
            initialLeafCards={categoryLeafCards}
            onClose={() => setShowPracticeCategorySession(false)}
            isInstanceOwner={isInstanceOwner}
            onDelete={isInstanceOwner ? onDelete : undefined}
            onMove={isInstanceOwner ? onMove : undefined}
            categories={allCategories}
          />
        )}

        {showContextualPractice && categoryData.id > 0 && (
          <ContextualPracticeModal
            categoryId={categoryData.id}
            categoryName={categoryData.name}
            leafCardCount={categoryLeafCards.length}
            onClose={() => setShowContextualPractice(false)}
          />
        )}

        {showPracticePhrasesSession && categoryData.id > 0 && (
          <CategoryPracticePhrasesSession
            categoryId={categoryData.id}
            categoryName={categoryData.name}
            onClose={() => setShowPracticePhrasesSession(false)}
            initialLeafCards={categoryLeafCards}
          />
        )}
      </div>

      {showGenerateStory && (
        <GenerateStoryModal
          categoryId={categoryData.id}
          categoryName={categoryData.name}
          onClose={() => setShowGenerateStory(false)}
          onStorySaved={loadStories}
        />
      )}

      {showAddText && (
        <AddTextModal
          categoryId={categoryData.id}
          onClose={() => setShowAddText(false)}
          onSaved={loadStories}
        />
      )}

      {editingStory && (
        <AddTextModal
          categoryId={categoryData.id}
          storyId={editingStory.id}
          initialTitle={editingStory.title}
          initialText={editingStory.story}
          onClose={() => setEditingStory(null)}
          onSaved={loadStories}
        />
      )}

      {revisingStory && (
        <ReviseStoryModal
          storyId={revisingStory.id}
          storyText={revisingStory.story}
          onClose={() => setRevisingStory(null)}
          onReplace={async (text, wordsUsed) => {
            await apiClient.updateStory(categoryData.id, revisingStory.id, { story: text, words_used: wordsUsed.length ? wordsUsed : undefined });
            loadStories();
          }}
        />
      )}

      {openStoryCardId && (
        <CardDetail
          cardId={openStoryCardId}
          onClose={() => setOpenStoryCardId(null)}
          onSave={() => {}}
        />
      )}

      {showMoveModal && (
        <MoveToCollectionModal
          categoryId={categoryData.id}
          currentInstanceId={selectedInstanceId}
          onSuccess={() => {
            setShowMoveModal(false);
            onDelete(categoryData.id);
            onClose();
          }}
          onClose={() => setShowMoveModal(false)}
        />
      )}
    </div>
  );
};

